export type IndustryDetail = {
  slug: string
  // Aliases that should also resolve to this entry (legacy short
  // slugs we used in earlier versions). Both render the same page;
  // the dynamic route adds a 301 redirect from each alias → slug
  // so the canonical URL is always the legacy idukki.io path.
  aliases?: string[]
  name: string
  hue: number
  hero: { eyebrow: string; headline: string; sub: string }
  highlights: string[]
  useCases: Array<{ title: string; body: string }>
  recommendedLayouts: string[]
  recommendedSources: string[]
  benchmark: string
  // Headline metrics for the "Accelerate your success with UGC" band.
  stats?: Array<{ value: string; label: string }>
  // The 4-step lifecycle: Collect → Tag → Embed → Analyze.
  howItWorks?: Array<{ step: string; title: string; body: string; bullets: string[] }>
  // 6-chip "Why choose Idukki?" grid.
  whyChoose?: string[]
  // FAQ block at the bottom.
  faqs?: Array<{ q: string; a: string }>
  // Slug of the corresponding /industries/* page on idukki.io. Used
  // by the dynamic route to fall back to the imported page's H1,
  // meta description, body and hero image when present.
  legacySlug?: string
  // Slug of the companion deep-dive playbook article in
  // lib/copy/idukki-articles.ts. When set, the industry page renders
  // a "Read the playbook" CTA section linking to the article, and
  // the article in turn back-links to the industry page. Keep the
  // article slug shape `<industry-slug>-ugc-playbook` for the
  // convention to stay scannable.
  playbookSlug?: string
  // Live-imported imagery from idukki.io/industries/* pages, mirrored
  // locally under public/brand/industries-live/ by the asset import
  // script. Keys (hero, runway, lookbook, gallery...) match the
  // illustration's narrative role on the live page. The industry page
  // template picks the appropriate slot per section.
  liveAssets?: {
    hero?: string
    heroVideo?: string
    runway?: string
    lookbook?: string
    trends?: string
    routines?: string
    gallery?: string
    shoppable?: string
    analytics?: string
    surfaces?: { home?: string; pdp?: string; checkout?: string; gallery?: string }
  }
}

// Shared "why choose Idukki" chips and FAQ stems used as defaults by
// industries that don't override them. Keeps copy cohesive.
const COMMON_WHY_CHOOSE = [
  'Intelligent profanity filter',
  'Automated rights management',
  'SEO-friendly widgets',
  '24×7 support',
  'No-code integration',
  'Mobile-responsive widgets',
]
const COMMON_HOW_IT_WORKS = [
  {
    step: 'COLLECT',
    title: 'Curate a runway of authentic content for your brand',
    body: 'Gather a diverse collection of user-generated content from social media, creating real-world inspiration for your shoppers.',
    bullets: [
      'Aggregate photos, videos and reviews from Instagram, TikTok, YouTube and more',
      'Use AI-powered discovery to filter and find desired content easily',
      'Fetch content from multiple sources like hashtags (#), tags (@) and creator handles',
    ],
  },
  {
    step: 'TAG',
    title: 'Transform UGC into shoppable content on your website',
    body: 'Turn inspiring visuals into instant shopping opportunities by tagging products directly, so the path from photo to checkout is one tap.',
    bullets: [
      'Easily tag multiple products in a single image or video',
      'Use hotspots to create shoppable lookbooks from UGC',
      '3× your conversions by allowing direct checkout from UGC widgets',
    ],
  },
  {
    step: 'EMBED',
    title: 'Display customer lookbooks in seconds',
    body: 'Elevate the on-site experience with a curated collection of UGC, embedded with one line of code.',
    bullets: [
      'Choose from grid, carousel, pop-up, masonry and lookbook layouts',
      'Keep product information updated with automatic catalog sync',
      'Maintain site speed with our optimised, lazy-loaded embed runtime',
    ],
  },
  {
    step: 'ANALYZE',
    title: 'Uncover trends that drive sales',
    body: 'Gain deep insights into how UGC influences your brand’s performance. Use data-driven strategies to optimise campaigns and drive revenue growth.',
    bullets: [
      'Measure clicks, add-to-carts, checkouts to scale and repeat',
      'Track content performance across categories, products and trends',
      'Identify your most influential brand advocates and top-performing posts',
    ],
  },
]

export const industries: IndustryDetail[] = [
  {
    slug: 'fashion-and-apparels',
    aliases: ['fashion-apparel'],
    name: 'Fashion + apparel',
    hue: 340,
    hero: {
      eyebrow: 'Fashion',
      headline: 'Real outfits, real bodies, fewer returns.',
      sub: 'Fit is the hardest thing to sell from a studio shot and the top reason fashion comes back. Tag your customers’ outfit photos, fit-checks and try-on hauls straight to the product, so a shopper sees the cut on a body like theirs before they buy.',
    },
    highlights: [
      'Outfit photos, fit-checks, before/after slider for try-on hauls',
      'Hashtag campaigns across Instagram + TikTok for new drops',
      'AI product tagging on every photo, hotspot to PDP',
      'Klaviyo flows with dynamic UGC blocks per segment',
    ],
    useCases: [
      {
        title: 'Home page',
        body: 'Lead with a social wall of customer outfits in the hero. New visitors see the clothes on real people before they reach a single studio shot.',
      },
      {
        title: 'PDP',
        body: 'Replace studio imagery on the size guide with fit-check UGC, +24% AOV in our reference cohort.',
      },
      {
        title: 'Lookbook',
        body: 'Compose a seasonal lookbook from your top 30 customer outfits. Hotspot every product.',
      },
      {
        title: 'Email + SMS',
        body: 'Klaviyo blocks pull the latest UGC by category, refreshed per send.',
      },
    ],
    recommendedLayouts: [
      'Grid + before/after',
      'Stories carousel',
      'Lookbook',
      'Mini-frame on PDP',
    ],
    recommendedSources: ['Instagram', 'TikTok', 'Pinterest', 'Trustpilot reviews'],
    benchmark: '+34% average order value when UGC replaces studio imagery on PDPs (n=46 brands).',
    stats: [
      { value: '5×', label: 'Conversion rate' },
      { value: '50%', label: 'Higher time spent on site' },
      { value: '80%', label: 'Less manual content review' },
      { value: 'Zero', label: 'Site-speed impact' },
    ],
    howItWorks: COMMON_HOW_IT_WORKS,
    whyChoose: COMMON_WHY_CHOOSE,
    faqs: [
      {
        q: 'Can UGC help reduce returns for my fashion brand?',
        a: 'Yes. UGC gives potential customers authentic views of your products on real people, helping shoppers understand fit, look, and styling in real-life situations. Setting realistic expectations cuts return rates because customers are more likely to be satisfied with what they receive.',
      },
      {
        q: 'How does Idukki ensure that the UGC displayed matches my brand’s aesthetic?',
        a: 'AI-powered moderation + manual approval queues let you set a brand-aesthetic baseline once. Anything that doesn’t fit gets filtered automatically; the rest queues for one-click approval.',
      },
      {
        q: 'Can Idukki help showcase different sizes and body types for my clothing line?',
        a: 'Yes, Claude vision auto-tags posts by visible size + body type so you can sort collection pages by representation. Shoppers can self-filter to "people with my body type", which routinely lifts AOV by 18-22% in fashion cohorts.',
      },
      {
        q: 'How can I use Idukki to create shoppable lookbooks from customer photos?',
        a: 'Drag-and-drop the Lookbook layout, hotspot every product, publish as a flipbook. Hosted on the homepage or any landing page.',
      },
      {
        q: 'How does Idukki handle rights management for user-generated content in the fashion industry?',
        a: 'Auto-DM rights flow with explicit consent capture. Every post on a public surface has a verified rights record + audit trail. GDPR + CCPA compliant.',
      },
    ],
    legacySlug: 'fashion-and-apparels',
    liveAssets: {
      hero: '/brand/industries-live/fashion-hero.webp',
      heroVideo: '/brand/industries-live/fashion-influencer.webm',
      runway: '/brand/industries-live/fashion-runway.webp',
      lookbook: '/brand/industries-live/fashion-lookbook.webp',
      trends: '/brand/industries-live/fashion-trends.webp',
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'fashion-and-apparels-ugc-playbook',
  },
  {
    slug: 'beauty-and-cosmetics',
    aliases: ['beauty-skincare'],
    name: 'Beauty + skincare',
    hue: 300,
    hero: {
      eyebrow: 'Beauty + skincare',
      headline: 'Shoppers buy when they see it work on skin like theirs.',
      sub: 'Shade, finish and texture are impossible to judge from a studio shot. Surface real routines, swatches and before/after clips from customers with the same skin, tagged straight to product, so a shopper can add to cart from the step that matters.',
    },
    highlights: [
      'Routine reels and tutorial videos with chapter-aware tagging',
      'Ingredient storytelling pulled from long-form YouTube reviews',
      'Reviews from Trustpilot, Yotpo, Loox, Okendo aggregated as standalone UGC',
      'Before/after slider for skin transformations',
    ],
    useCases: [
      {
        title: 'PDP',
        body: 'Routine video alongside ingredients deck. Chapter timestamps map to product hotspots.',
      },
      {
        title: 'Collection',
        body: 'Sort by skin concern using UGC with auto-applied tags from Claude vision.',
      },
      {
        title: 'Quiz funnel',
        body: 'Match UGC to quiz outcome, “oily skin” sees real oily-skin customer routines.',
      },
    ],
    recommendedLayouts: ['Stories carousel', 'Before/after', 'Mini-frame on PDP', 'Lookbook'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Trustpilot', 'Feefo'],
    benchmark:
      '+28% repeat purchase rate when ingredient stories are surfaced on the PDP (n=18 brands).',
    stats: [
      { value: '2-3×', label: 'PDP conversion with review UGC' },
      { value: '+28%', label: 'Repeat purchase, ingredient stories' },
      { value: '−21%', label: 'Returns from real-skin demos' },
      { value: 'Zero', label: 'Site-speed impact' },
    ],
    howItWorks: COMMON_HOW_IT_WORKS,
    whyChoose: COMMON_WHY_CHOOSE,
    faqs: [
      {
        q: 'Why does UGC convert so well for beauty and skincare?',
        a: 'Beauty is high-consideration and skin-tone, finish and texture are impossible to judge from a studio shot. Real routines, swatches and before/after clips from customers with similar skin let a shopper see the result on someone like them, which is the single biggest driver of purchase confidence in the category. Consolidated industry data (Bazaarvoice, Nosto) puts conversion uplift from authentic visual reviews at roughly 2-3× a no-UGC baseline.',
      },
      {
        q: 'How does Idukki handle shade and skin-type representation?',
        a: 'Claude vision auto-tags posts by visible skin tone, type and concern, so collection pages can be filtered to "people like me". Letting shoppers self-select representation is what lifts AOV and cuts returns in beauty cohorts.',
      },
      {
        q: 'Can I surface ingredient and tutorial content on the PDP?',
        a: 'Yes. Chapter-aware tagging maps timestamps in a routine or tutorial video to product hotspots, so a shopper jumps straight to the step that matters and can add to cart from the clip.',
      },
      {
        q: 'How do you keep before/after content compliant?',
        a: 'Auto-DM rights capture logs explicit consent for every before/after post, with a tamper-evident audit trail. That keeps testimonial-style claims defensible under FTC and ASA guidance.',
      },
    ],
    legacySlug: 'beauty-and-cosmetics',
    liveAssets: {
      hero: '/brand/industries-live/beauty-routines.webp',
      gallery: '/brand/industries-live/beauty-gallery.webp',
      shoppable: '/brand/industries-live/beauty-shoppable.webp',
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'beauty-and-cosmetics-ugc-playbook',
  },
  {
    slug: 'accessories-and-jewelery',
    aliases: ['accessories-jewellery'],
    name: 'Accessories + jewellery',
    hue: 30,
    hero: {
      eyebrow: 'Accessories + jewellery',
      headline: 'See how a piece sits on a real wrist before you buy.',
      sub: 'Real-world photos let buyers see scale, weight and how a piece sits on real skin. The single biggest gap between online and in-store, closed.',
    },
    highlights: [
      'On-body photography from real customers',
      'Hand-pour, packaging, gifting unboxing UGC',
      '360-degree video imports from creator partners',
      'Trustpilot + Google Reviews aggregation',
    ],
    useCases: [
      {
        title: 'PDP',
        body: 'On-body photo carousel directly under the product gallery. Tap to see size on different wrists / necks.',
      },
      {
        title: 'Wedding ring landing',
        body: 'A curated lookbook of real customer rings, styled by metal and stone.',
      },
      { title: 'Gift guide', body: 'A seasonal guide composed from gifting unboxings.' },
    ],
    recommendedLayouts: ['Carousel', 'Lookbook', 'Grid', 'Mini-frame on PDP'],
    recommendedSources: ['Instagram', 'Pinterest', 'YouTube', 'Trustpilot'],
    benchmark: '+19% PDP add-to-cart when on-body UGC sits above the size selector.',
    legacySlug: 'accessories-and-jewelery',
    liveAssets: {
      hero: '/brand/industries-live/jewellery-gallery.webp',
      shoppable: '/brand/industries-live/jewellery-shoppable.webp',
      analytics: '/brand/industries-live/jewellery-analytics.webp',
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'accessories-and-jewelery-ugc-playbook',
  },
  {
    slug: 'home-lifestyle',
    name: 'Home + lifestyle',
    hue: 200,
    hero: {
      eyebrow: 'Home + lifestyle',
      headline: 'Real homes. Real installs. Real conviction to buy.',
      sub: 'Homes look different in every region, every season, every taste. UGC from your customers shows yours fitting all of them.',
    },
    highlights: [
      'Pinterest + Instagram aggregation across rooms and styles',
      'Before/after for renovations and refurbishments',
      'Room-style filters auto-applied via AI tagging',
      'Hashtag campaigns for room-of-the-month',
    ],
    useCases: [
      {
        title: 'Collection',
        body: 'Browse “living rooms”, “kitchens”, “bedrooms” each populated with real customer installs.',
      },
      {
        title: 'PDP',
        body: 'How a sofa looks across 12 different real living rooms, not just the one studio shot.',
      },
      {
        title: 'Inspiration hub',
        body: 'A Pinterest-style mood board that pulls in real customer pins.',
      },
    ],
    recommendedLayouts: ['Masonry', 'Mood-board', 'Lookbook', 'Before/after'],
    recommendedSources: ['Pinterest', 'Instagram', 'YouTube', 'Trustpilot'],
    benchmark: '+41% click-through on collection pages with native customer-install UGC.',
    liveAssets: {
      hero: '/brand/industries-live/home-first-impressions.webp',
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'home-lifestyle-ugc-playbook',
  },
  {
    slug: 'furniture',
    name: 'Furniture',
    hue: 20,
    hero: {
      eyebrow: 'Furniture',
      headline: 'See the sofa in a real room, not a studio set.',
      sub: 'Furniture is bought once and lived with for years. Real-life UGC closes the conviction gap that studio shots leave open.',
    },
    highlights: [
      'Scale-in-context photos: how the piece looks in real rooms',
      'Multi-angle UGC for upholstery and finish quality',
      'Trustpilot + Google reviews on delivery + assembly',
      'YouTube long-form unboxings with chapter tagging',
    ],
    useCases: [
      {
        title: 'PDP',
        body: 'Replace one studio image with three customer photos at the same angle.',
      },
      {
        title: 'Delivery + assembly',
        body: 'Real customer videos answer “will it fit through my door?”',
      },
      { title: 'Inspiration', body: 'Mood-board of customer rooms organised by style + size.' },
    ],
    recommendedLayouts: ['Grid', 'Carousel', 'Mood-board', 'Mini-frame on PDP'],
    recommendedSources: ['Instagram', 'Pinterest', 'YouTube', 'Trustpilot'],
    benchmark: '+27% PDP dwell time when customer-room UGC replaces a studio shot.',
    legacySlug: 'furniture',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'furniture-ugc-playbook',
  },
  {
    slug: 'hospitality',
    name: 'Hospitality',
    hue: 190,
    hero: {
      eyebrow: 'Hospitality',
      headline: 'Guests believe other guests, not your hero shot.',
      sub: 'Guests trust other guests. Hotels, restaurants and venues that surface guest UGC outconvert glossy hero images by 30–47%.',
    },
    highlights: [
      'Guest photos + reviews aggregated from Instagram, TripAdvisor, Google',
      'Geo-tagged content for property + neighbourhood',
      'Event walls during conferences + venue takeovers',
      'Real-time digital signage on lobby + venue screens',
    ],
    useCases: [
      { title: 'Property page', body: 'Guest gallery underneath the room booking widget.' },
      { title: 'Restaurant menu', body: 'Real diner photos of every signature dish.' },
      {
        title: 'Event wall',
        body: 'Real-time hashtag wall on a 4K screen during weddings + conferences.',
      },
    ],
    recommendedLayouts: ['Social wall', 'Carousel', 'Digital signage', 'Mini-frame'],
    recommendedSources: ['Instagram', 'TripAdvisor', 'Google Reviews', 'X / Twitter'],
    benchmark: '+47% engagement on destination pages with traveller UGC (n=12 properties).',
    legacySlug: 'hospitality',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'hospitality-ugc-playbook',
  },
  {
    slug: 'consumer-electronics',
    name: 'Consumer electronics',
    hue: 210,
    hero: {
      eyebrow: 'Consumer electronics',
      headline: 'Tech buyers trust the reviewer’s unboxing over your spec sheet.',
      sub: 'Tech buyers research hard. Real reviewer videos, setup walkthroughs and unboxings convert better than any spec sheet.',
    },
    highlights: [
      'YouTube long-form reviews with chapter-aware product tagging',
      'Setup walkthroughs and use-case demos from creators',
      'Spec-comparison overlays from third-party reviewers',
      'Long-tail Trustpilot + Reddit + forum review aggregation',
    ],
    useCases: [
      { title: 'PDP', body: 'Reviewer video carousel with timestamp links to spec moments.' },
      { title: 'Comparison page', body: 'Real customer side-by-side videos comparing models.' },
      { title: 'Support hub', body: 'Setup walkthroughs reduce support tickets by 18%.' },
    ],
    recommendedLayouts: ['Channel hub', 'Carousel', 'Mini-frame on PDP', 'Lookbook'],
    recommendedSources: ['YouTube', 'Trustpilot', 'X / Twitter', 'Instagram'],
    benchmark: '+19% PDP dwell-time when video reviews replace static spec lists.',
    legacySlug: 'consumer-electronics',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'consumer-electronics-ugc-playbook',
  },
  {
    slug: 'sporting-goods',
    name: 'Sporting goods + fitness',
    hue: 0,
    hero: {
      eyebrow: 'Sporting goods',
      headline: 'Athletes buy the gear they see other athletes train in.',
      sub: 'Athletes pick gear that other athletes use. UGC from real training sessions is the highest-converting content you can show.',
    },
    highlights: [
      'Athlete content + training transformations',
      'Gear-in-action loops on the PDP',
      'Affiliate creator attribution with revenue share tracking',
      'Race + event walls during sponsorships',
    ],
    useCases: [
      { title: 'PDP', body: 'Real athlete using the kit in race conditions.' },
      { title: 'Training program', body: 'UGC from program graduates as social proof.' },
      { title: 'Event coverage', body: 'Live hashtag wall during sponsored races.' },
    ],
    recommendedLayouts: ['Carousel', 'Stories', 'Mini-frame on PDP', 'Social wall'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Strava'],
    benchmark: '+33% conversion on training-program pages with athlete UGC (n=9 brands).',
    legacySlug: 'sporting-goods',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'sporting-goods-ugc-playbook',
  },
  {
    slug: 'b2b-saas',
    name: 'B2B + SaaS',
    hue: 230,
    hero: {
      eyebrow: 'B2B + SaaS',
      headline: 'Customer logos, case-study video and LinkedIn quotes, everywhere.',
      sub: 'B2B buyers want proof. Idukki turns LinkedIn posts, case-study video and customer logos into a coherent social-proof system across your site.',
    },
    highlights: [
      'LinkedIn quote-card auto-generation from long posts',
      'Customer logo wall with click-through to case studies',
      'YouTube case-study video with chapter-aware overlays',
      'Demo-booking attribution per case-study page',
    ],
    useCases: [
      { title: 'Home page', body: 'Logo wall + a 30-second auto-rotating quote band.' },
      { title: 'PDP / use-case', body: 'A relevant customer case-study video above the fold.' },
      {
        title: 'Pricing',
        body: 'Customer quotes by plan tier, social proof at the point of decision.',
      },
    ],
    recommendedLayouts: ['Logo wall', 'Quote wall', 'Carousel', 'Channel hub'],
    recommendedSources: ['LinkedIn', 'YouTube', 'X / Twitter', 'G2'],
    benchmark: '+2.4× demo bookings when case-study video runs above the fold.',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'b2b-saas-ugc-playbook',
  },
  {
    slug: 'baby-and-kids',
    name: 'Baby + kids',
    hue: 280,
    hero: {
      eyebrow: 'Baby + kids',
      headline: 'Parents buy what other parents trusted with their own kids.',
      sub: 'Baby + kids buyers spend longer on PDPs than any other vertical, they\'re evaluating risk, not aesthetics. Idukki surfaces parent reviews, real-room photos and "we used it from 6 to 24 months" longitudinal stories.',
    },
    highlights: [
      'Parent reviews with verified-buyer + age-of-child filter',
      'Safety + materials stories surfaced above the fold',
      'Longitudinal "we used this from X months" growth journeys',
      'Pediatrician + creator UGC labelled clearly',
    ],
    useCases: [
      {
        title: 'PDP',
        body: 'Age-filtered reviews + a "what parents bought next" rail of related kit.',
      },
      {
        title: 'Collection',
        body: 'Stage-of-life browsing: newborn / weaning / toddler / pre-school.',
      },
      { title: 'Email', body: 'Re-purchase prompts at the age + size milestones the cohort hits.' },
    ],
    recommendedLayouts: ['Reviews wall', 'Story rail', 'PDP gallery', 'Lookbook'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Trustpilot'],
    benchmark: '+38% PDP conversion when an age-filtered review block sits above the buy box.',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'baby-and-kids-ugc-playbook',
  },
  {
    slug: 'health-and-wellness',
    aliases: ['health-wellness', 'wellness', 'supplements'],
    name: 'Health + wellness',
    hue: 156,
    hero: {
      eyebrow: 'Health + wellness',
      headline: 'Trust is the conversion event. UGC is the trust.',
      sub: "Wellness shoppers don't believe brand copy and they can't legally be told what a product will do. Idukki surfaces the verified-buyer reviews, the routine photos and the longitudinal \"I've been on this for 90 days\" stories that actually move the cart.",
    },
    highlights: [
      'Verified-buyer reviews with consumption-window filtering (30 / 60 / 90 days)',
      'Routine + ritual UGC labelled separately from sponsored creator content',
      'Compliance-safe rights workflow: blocked health-claim auto-filter on submission',
      'Subscription-renewal cohorts surfaced on PDP ("8 months in" stories)',
    ],
    useCases: [
      {
        title: 'Supplements PDP',
        body: 'Day-30 / day-60 / day-90 routine photos above the buy box, plus a verified-buyer rolling rating from the last 90 days only.',
      },
      {
        title: 'Sleep + recovery',
        body: 'Home-setup gallery (Eight Sleep, Hatch, ChiliPad-style) with sleep-score before/after stories from real customers.',
      },
      {
        title: "Women's wellness",
        body: 'Discreet, opt-in verified-buyer reviews with strong moderation. Period-care + sexual-wellness categories handled with category-specific UX.',
      },
      {
        title: 'Subscription renewal',
        body: 'Renewal-page UGC rail showing "X months in" customers, calibrated to your churn cohorts.',
      },
    ],
    recommendedLayouts: ['Reviews wall', 'Before / after slider', 'Story rail', 'Routine timeline'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Trustpilot', 'Feefo'],
    benchmark:
      '+54% subscription-renewal lift on PDPs that surface a 60-day-in cohort story above the renewal CTA. Independent supplement brand audit, n=4 (composite).',
    stats: [
      { value: '+54%', label: 'subscription renewal lift on cohort-story PDPs' },
      { value: '+38%', label: 'first-order conversion when routine UGC sits above the buy box' },
      {
        value: '14×',
        label: 'weight AI shopping agents give verified-buyer reviews vs unverified',
      },
    ],
    whyChoose: [
      'Compliance-safe rights flow (blocked-claim filter on submission)',
      'Verified-buyer + consumption-window review filter',
      'Routine photos labelled separately from sponsored creator UGC',
      'Subscription-cohort UGC on renewal pages',
      'AWS eu-west-2 data residency, audit-log on every approval',
      'No medical-claim auto-generation in AI captions or summaries',
    ],
    faqs: [
      {
        q: 'Is Idukki HIPAA-ready for health brands?',
        a: 'A BAA is available on Enterprise plans. We do not process PHI by default, the platform handles marketing-side UGC, ratings and reviews; clinical data stays in your separately-permissioned systems.',
      },
      {
        q: 'How do you keep us out of FDA / MHRA / ASA trouble?',
        a: 'A blocked-claim filter scans every submission for the language regulators care about (cure / treat / diagnose / prevent / FDA-approved). Flagged posts route to manual review before they can publish. We never auto-generate medical claims in AI captions or summaries.',
      },
      {
        q: 'Do you support sensitive categories (period, sexual wellness)?',
        a: 'Yes. Both categories have category-specific UX: opt-in verified-buyer reviews, anonymised display options, discreet moderation queues and a separately-tuned brand-safety model.',
      },
      {
        q: 'How is data residency handled?',
        a: 'AWS eu-west-2 (London) by default. Pinned per workspace, never replicated cross-region. Additional regions available on request for Enterprise.',
      },
    ],
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'health-and-wellness-ugc-playbook',
  },
  {
    slug: 'food-and-beverage',
    aliases: ['food', 'beverage', 'food-beverage', 'fnb'],
    name: 'Food + beverage',
    hue: 18,
    hero: {
      eyebrow: 'Food + beverage',
      headline: "The dish in the customer's kitchen sells better than the dish in your studio.",
      sub: 'Food and drink is the most visually-native category there is. Idukki turns the recipe shots, the unboxing reels, the pairing notes and the "this is now my morning ritual" content your customers already make into the storefront\'s primary creative.',
    },
    highlights: [
      'Recipe-card UGC with auto-tagged ingredients linking back to PDPs',
      'Tasting-notes + pairing UGC labelled and filterable by flavour profile',
      'Unboxing + gift-moment reels surfaced on PDP and gift-finder pages',
      'Subscription cohort photos ("3 months of coffee", "30-day cleanse")',
    ],
    useCases: [
      {
        title: 'Coffee + tea PDP',
        body: 'Brew-method UGC grouped by extraction (V60, espresso, French press), each tile linking back to the bag of beans and the gear used to brew it.',
      },
      {
        title: 'Wine + spirits',
        body: 'Food-pairing UGC surfaced on the PDP, plus an age-gated reviews wall that meets DSA + ASA rules for alcohol marketing.',
      },
      {
        title: 'Snacks + better-for-you',
        body: 'Ingredient-led UGC ("no seed oils, no gum") that builds the trust copy can\'t. Recipe remixes drive repeat purchase.',
      },
      {
        title: 'Gifting + hampers',
        body: 'Unboxing-moment reels on hamper PDPs. Gift-finder pages pull UGC sorted by occasion (Diwali, holidays, host gifts).',
      },
    ],
    recommendedLayouts: ['Recipe wall', 'Reviews wall', 'Story rail', 'Lookbook', 'Reel carousel'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Pinterest', 'Trustpilot'],
    benchmark:
      '+47% PDP conversion on coffee + tea SKUs when a brew-method UGC rail sits above the buy box. Composite from three Idukki specialty-coffee customers, n=3.',
    stats: [
      { value: '+47%', label: 'PDP conversion on brew-method UGC rails' },
      { value: '+62%', label: 'gift-page conversion when an unboxing reel sits above the fold' },
      { value: '3.4×', label: 'recipe-card shoppable-ingredient CTR vs standard PDP add-to-cart' },
    ],
    whyChoose: [
      'Recipe-card UGC with auto-tagged ingredients',
      'Age-gated review walls (alcohol, CBD-adjacent)',
      'Flavour-profile + dietary filter on reviews',
      'Subscription-renewal UGC cohorts',
      'EU + UK + US food-marketing compliance posture',
      'Allergy / ingredient call-out moderation rules',
    ],
    faqs: [
      {
        q: 'Does Idukki handle alcohol-marketing compliance?',
        a: 'Yes. Age-gating on widget mount, regulated-language moderation (no health claims on alcohol), ASA + DSA-aligned warning footers, no minors in flagged UGC. UK MUR + EU DSA defaults built in.',
      },
      {
        q: 'Can recipe-card UGC link back to multiple SKUs at once?',
        a: 'Yes. The vision tagger recognises every product in a recipe shot and renders a multi-hotspot tile, so a single bowl can link out to four SKUs without you tagging them by hand.',
      },
      {
        q: 'How do you handle ingredient + allergy claims in UGC?',
        a: 'Submission scan blocks "no seed oils / gluten-free / organic" wording when the SKU isn\'t certified for the claim, plus a separately-tunable allergen lexicon. Flagged posts queue for manual review before publish.',
      },
      {
        q: 'Do you support seasonal gift-finder UGC?',
        a: 'Yes. Occasions (Diwali, Christmas, host gifts, Eid, Lunar New Year) are first-class tags on every piece of UGC, and the gift-finder widget filters and ranks by occasion + price band.',
      },
    ],
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'food-and-beverage-ugc-playbook',
  },
  {
    slug: 'automobiles',
    aliases: ['auto', 'automotive', 'cars', 'ev'],
    name: 'Automobiles',
    hue: 220,
    hero: {
      eyebrow: 'Automobiles + EV',
      headline:
        'The owner&rsquo;s review at twelve months sells the car. The brochure rarely does.',
      sub: 'Cars are a long-cycle, high-consideration purchase. Buyers spend weeks reading owners’ forums, watching YouTube reviewers and lurking on Reddit before they ever click "book a test drive". Idukki turns the ownership reels, the road-trip content and the "one year in" updates your customers post into the storefront’s real test drive.',
    },
    highlights: [
      'Ownership-tenure filter on reviews ("3 months in" / "12 months in" / "3 years in")',
      'Road-trip + charging-stop UGC mapped to dealer-locator + service-network pages',
      'Test-drive booking gallery tied to the closest creator-owner content',
      'After-care UGC for EV software updates and recall communications',
    ],
    useCases: [
      {
        title: 'Model PDP',
        body: 'Ownership-tenure reviews above the buy box, plus a road-trip reel rail underneath that links to the dealer locator with the nearest in-stock unit.',
      },
      {
        title: 'EV charging map',
        body: 'Charging-stop UGC plotted onto your route planner so a buyer can see what a real charge stop looks like at the supercharger they’d use on a Friday-night trip.',
      },
      {
        title: 'Test-drive booking',
        body: 'Each test-drive slot pulls in the most-watched creator-owner reel for that trim, so the buyer arrives at the dealership already half-sold.',
      },
      {
        title: 'Modification community',
        body: 'Wheels, wraps, software-mods UGC labelled separately so OEM warranty messaging stays clean while the enthusiast surface stays alive.',
      },
    ],
    recommendedLayouts: ['Reviews wall', 'Reel carousel', 'Story rail', 'Lookbook', 'Gallery'],
    recommendedSources: ['YouTube', 'Instagram', 'TikTok', 'Reddit', 'Trustpilot'],
    benchmark:
      '+34% test-drive booking rate on model PDPs that surface a "12 months in" owner story above the booking CTA. Composite, three EV / luxury OEM customers, n=3.',
    stats: [
      { value: '+34%', label: 'test-drive booking lift on ownership-tenure PDPs' },
      { value: '4.2×', label: 'creator-owner reel CTR vs OEM-produced trim videos' },
      { value: '90d', label: 'avg shopper consideration window before booking a test drive' },
    ],
    whyChoose: [
      'Ownership-tenure review filter (3mo / 12mo / 3yr)',
      'Mod / aftermarket UGC labelled separately from OEM warranty content',
      'Dealer-locator + UGC route planner integration',
      'Recall + software-update comms with verified-owner UGC',
      'GDPR + UK GDPR + state-DMV privacy posture for owner data',
      'EU + UK distance-selling regulation footers on PDP',
    ],
    faqs: [
      {
        q: 'Can dealers and HQ both publish UGC under one roof?',
        a: 'Yes. Workspaces are scoped per dealer-group + per HQ, with HQ getting an over-the-top moderation and brand-safety lock. Dealers can publish locally but cannot override HQ-flagged content.',
      },
      {
        q: 'How do you keep modification UGC separate from OEM warranty content?',
        a: 'Submission tags route content into separate moderation lanes. OEM warranty content publishes only to OEM-controlled surfaces; modification content publishes to community surfaces and is filtered by default on PDP unless the buyer opts in.',
      },
      {
        q: 'Do you handle recall and software-update communications?',
        a: 'Yes. Verified-owner UGC can be surfaced alongside OEM recall + OTA-update notifications, with the audit log and rights-clearance trail every legal team wants.',
      },
      {
        q: 'What about EU + UK distance-selling rules?',
        a: 'Standard PDP footers, 14-day cooling-off language and price-disclosure compliance are built into the widget. UK MUR + EU DSA defaults applied by region.',
      },
    ],
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'automobiles-ugc-playbook',
  },
  {
    slug: 'edtech',
    aliases: ['education', 'edu', 'e-learning', 'online-learning', 'edu-tech'],
    name: 'EdTech',
    hue: 264,
    hero: {
      eyebrow: 'EdTech + online learning',
      headline: 'Outcomes are the proof. UGC is how shoppers find them.',
      sub: 'Education is bought on faith. The shopper is investing months of their life and meaningful money in something whose outcome they can’t verify until afterwards. Idukki surfaces the streak photos, the "I got the job" stories and the before/after skill demos that show the outcome is real.',
    },
    highlights: [
      '"I got the job" outcome stories with role + salary band metadata',
      'Streak + progress UGC labelled by week / month / year of learning',
      'Before/after skill demos (code repos, design portfolios, language fluency clips)',
      'Verified-completion badge on graduate-shared content',
    ],
    useCases: [
      {
        title: 'Course PDP',
        body: 'A "what graduates do next" rail above the enrolment CTA. Outcome stories filterable by role, by salary band, by industry.',
      },
      {
        title: 'Tutor / mentor profile',
        body: 'Verified student reviews with tenure-of-study filter, plus a streak gallery showing real student work the tutor has guided.',
      },
      {
        title: 'Subscription renewal',
        body: 'Long-tenure learner stories ("18 months in, finally fluent in Japanese") surfaced on the renewal page. Cuts churn on the 90-day cliff most edtech subs hit.',
      },
      {
        title: 'Demo-day showcase',
        body: 'A live wall of cohort-graduate work that doubles as an enrolment proof point AND a portfolio for the cohort.',
      },
    ],
    recommendedLayouts: ['Reviews wall', 'Story rail', 'Lookbook', 'Gallery', 'Reel carousel'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'LinkedIn', 'Trustpilot'],
    benchmark:
      '+58% enrolment conversion on course PDPs that lead with verified outcome stories above the enrolment CTA. Composite, four EdTech customers, n=4.',
    stats: [
      { value: '+58%', label: 'enrolment conversion on outcome-led course PDPs' },
      { value: '−29%', label: 'churn on 90-day renewal pages with long-tenure learner stories' },
      {
        value: '3.1×',
        label: 'LinkedIn "I got the job" post engagement vs studio-shot grad photos',
      },
    ],
    whyChoose: [
      'Outcome-story metadata: role, salary band, industry',
      'Verified-completion badge on graduate UGC',
      'Tenure-of-study filter on tutor and mentor reviews',
      'COPPA + FERPA-aware moderation for under-18 learners',
      'Multi-language UGC (28 locales) for global learner cohorts',
      'LinkedIn-native rights flow for "I got the job" posts',
    ],
    faqs: [
      {
        q: 'Is Idukki COPPA + FERPA-aware for K-12 platforms?',
        a: 'Yes. K-12 workspaces default to stricter rights flows: under-18 UGC requires parent / guardian consent capture, faces are blurred unless explicitly permitted, and submissions are routed through a tightened brand-safety lane.',
      },
      {
        q: 'How do "I got the job" stories get verified?',
        a: 'LinkedIn-native rights flow: the graduate connects their LinkedIn, we pull the role + employer with their consent, and the post renders with a verified-employment badge. Sales team can opt in to a follow-up flow for case-study leads.',
      },
      {
        q: 'Can we A/B test outcome-story placement on the PDP?',
        a: 'Yes. Welch z-test on enrolment conversion, sequential MAB for traffic allocation, auto-promotes the winning placement. Bias-corrected for selection effects when measuring renewal.',
      },
      {
        q: 'Does it work across 28 languages?',
        a: 'Yes. Moderation, tagging and rights-request DMs all run across 28 locales. Useful for Duolingo-style global learner bases or Reforge-style enterprise cohorts with non-English speakers.',
      },
    ],
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'edtech-ugc-playbook',
  },
  {
    slug: 'travel-and-experiences',
    aliases: ['travel', 'experiences', 'tours', 'activities'],
    name: 'Travel + experiences',
    hue: 195,
    hero: {
      eyebrow: 'Travel + experiences',
      headline: 'The traveller’s reel sells the trip. Your brochure rarely does.',
      sub: 'Travel buyers are visual-first. They’re booking a feeling they want to recreate, the sunset, the dive, the meal. Idukki turns the destination reels, the itinerary recaps and the packing lists your past customers already make into the discovery and booking surface.',
    },
    highlights: [
      'Destination-tagged UGC plotted onto an interactive trip map',
      'Itinerary recap reels surfaced on tour / activity PDPs',
      'Packing-list UGC tied to climate, activity type and season',
      'Cohort gallery: "everyone who went on this trip last month"',
    ],
    useCases: [
      {
        title: 'Tour / activity PDP',
        body: 'A reel rail of past traveller reels above the booking CTA, plus a packing-list block sourced from real travellers who took the same tour.',
      },
      {
        title: 'Destination landing page',
        body: 'A UGC wall of best-moment shots from the city / island / route, filterable by month, by traveller type (solo / family / couple).',
      },
      {
        title: 'Cruise + multi-day',
        body: 'A day-by-day itinerary visual where every day shows real-traveller UGC from that exact port stop or excursion.',
      },
      {
        title: 'Travel gear DTC',
        body: 'Pack-it-with stories: a single carry-on photo links out to every SKU it carries, with the route + climate the gear survived.',
      },
    ],
    recommendedLayouts: ['Map widget', 'Story rail', 'Reviews wall', 'Lookbook', 'Reel carousel'],
    recommendedSources: [
      'Instagram',
      'TikTok',
      'YouTube',
      'Pinterest',
      'TripAdvisor',
      'Trustpilot',
    ],
    benchmark:
      '+41% booking conversion on tour PDPs that surface a recent-traveller reel above the CTA, plus a packing-list block sourced from the same cohort. Composite, three OTA + tour-operator customers, n=3.',
    stats: [
      {
        value: '+41%',
        label: 'booking conversion on tour PDPs with traveller-reel + packing block',
      },
      {
        value: '2.8×',
        label: 'destination landing page time-on-page with UGC wall vs hero photo only',
      },
      {
        value: '54%',
        label: 'of travel shoppers cite social UGC as their top inspiration source (Mintel, 2025)',
      },
    ],
    whyChoose: [
      'Destination + climate + season-aware tagging',
      'Map widget plotting UGC onto routes + ports',
      'Cohort-of-the-month gallery for repeat tours',
      'Multi-language UGC for global traveller audiences',
      'Packing-list UGC tied to gear PDPs',
      'TripAdvisor + Trustpilot syndication built in',
    ],
    faqs: [
      {
        q: 'Can the tour PDP show ONLY content from the specific tour?',
        a: 'Yes. Submission metadata captures the SKU / tour code, so the PDP can scope its UGC to that exact tour and date range. Useful when "Tour of Tuscany A" runs a different itinerary from "Tour of Tuscany B".',
      },
      {
        q: 'How do you handle seasonal content?',
        a: 'Every piece of UGC carries a season + month tag. Destination pages auto-rotate, so a Bali landing page surfaces rainy-season UGC in November and dry-season UGC in May. Travellers see what they’ll actually experience.',
      },
      {
        q: 'Do you support multi-language reviews?',
        a: 'Yes, across 28 locales. Particularly useful for OTAs serving Japanese + Korean + Mandarin-reading buyers on the same destination page.',
      },
      {
        q: 'How do TripAdvisor + Trustpilot reviews integrate?',
        a: 'Direct API sync. Reviews flow into the same widget runtime your social UGC uses, with verified-traveller badges intact and the source platform clearly labelled.',
      },
    ],
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'travel-and-experiences-ugc-playbook',
  },
  {
    slug: 'pet-care',
    name: 'Pet care',
    hue: 90,
    hero: {
      eyebrow: 'Pet care',
      headline: 'Pet parents buy the before-and-after they can see.',
      sub: "Pet-care buyers convert on two signals: visible result (before-and-after coat / weight / mood) and longevity (they've been feeding this for months). Idukki gives both surfaces native widgets.",
    },
    highlights: [
      'Before-and-after sliders for coat, weight, energy outcomes',
      'Repeat-buyer storytelling, "we\'ve been on this for 8 months"',
      'Breed / weight / age filter on the reviews wall',
      'Vet-creator UGC labelled separately from pet-parent UGC',
    ],
    useCases: [
      {
        title: 'PDP',
        body: 'Before-and-after slider + filterable reviews wall by breed + weight.',
      },
      { title: 'Subscription', body: 'Renewal page with "X months in" pet-parent stories.' },
      { title: 'Email', body: "Re-stock cadence prompts tied to the cohort's feeding-cycle data." },
    ],
    recommendedLayouts: ['Before / after slider', 'Reviews wall', 'Gallery', 'Lookbook'],
    recommendedSources: ['Instagram', 'TikTok', 'YouTube', 'Trustpilot'],
    benchmark:
      '+44% subscription renewal on PDPs where a "6 months in" pet-parent story is above the fold.',
    liveAssets: {
      surfaces: {
        home: '/brand/industries-live/surface-home.webp',
        pdp: '/brand/industries-live/surface-pdp.webp',
        checkout: '/brand/industries-live/surface-checkout.webp',
        gallery: '/brand/industries-live/surface-gallery.webp',
      },
    },
    playbookSlug: 'pet-care-ugc-playbook',
  },
]
